import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Swal from 'sweetalert2';
import { obtenerFechaLocal } from '../utils/dateUtils';

const fmt = (n) => Number(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ESTADO_COLOR = {
  borrador:  '#6c757d',
  procesada: '#0d6efd',
  pagada:    '#28a745',
  anulada:   '#dc3545',
};

export default function NominaUniversal() {
  const navigate = useNavigate();
  const [tab, setTab]               = useState('periodos');
  const [periodos, setPeriodos]     = useState([]);
  const [resumen, setResumen]       = useState(null);
  const [detallePeriodo, setDetallePeriodo] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [quincenaDescuento, setQuincenaDescuento] = useState('primera');
  const [guardandoConfig, setGuardandoConfig]     = useState(false);

  // Preview / procesar
  const [modalProcesar, setModalProcesar] = useState(false);
  const [preview, setPreview]       = useState(null);
  const [loadPreview, setLoadPreview] = useState(false);
  const [formProcesar, setFormProcesar] = useState({
    fecha_inicio: '', fecha_fin: '', tipo_periodo: 'quincenal', notas: '',
  });
  // Ajustes individuales
  const [ajustes, setAjustes] = useState({}); // { [empleado_id]: { dias_trabajados, bonificaciones, otros_descuentos, ... } }

  const cargarPeriodos = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r, cfg] = await Promise.all([
        api.get('/nomina-universal'),
        api.get('/nomina-universal/resumen'),
        api.get('/configuracion'),
      ]);
      setPeriodos(p.data);
      setResumen(r.data);
      setQuincenaDescuento(cfg.data?.nomina_quincena_descuento || 'primera');
    } catch { } finally { setLoading(false); }
  }, []);

  const guardarConfigNomina = async () => {
    setGuardandoConfig(true);
    try {
      await api.put('/configuracion', { nomina_quincena_descuento: quincenaDescuento });
      Swal.fire({ icon: 'success', title: 'Guardado', text: 'Configuración de nómina actualizada', timer: 1800, showConfirmButton: false });
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo guardar la configuración' });
    } finally { setGuardandoConfig(false); }
  };

  useEffect(() => { cargarPeriodos(); }, [cargarPeriodos]);

  const verDetalle = async (id) => {
    try {
      const { data } = await api.get(`/nomina-universal/${id}`);
      setDetallePeriodo(data);
      setTab('detalle');
    } catch { Swal.fire('Error', 'No se pudo cargar el detalle', 'error'); }
  };

  // Calcular fechas según tipo de período y fecha actual
  const fechasParaTipo = (tipo) => {
    const hoy = new Date();
    const y = hoy.getFullYear();
    const m = String(hoy.getMonth() + 1).padStart(2, '0');
    const dia = hoy.getDate();
    const lastDay = new Date(y, hoy.getMonth() + 1, 0).getDate();
    if (tipo === 'quincenal') {
      if (dia <= 15) {
        return { inicio: `${y}-${m}-01`, fin: `${y}-${m}-15` };
      } else {
        return { inicio: `${y}-${m}-16`, fin: `${y}-${m}-${String(lastDay).padStart(2, '0')}` };
      }
    }
    // mensual / semanal
    return { inicio: `${y}-${m}-01`, fin: `${y}-${m}-${String(lastDay).padStart(2, '0')}` };
  };

  const abrirProcesar = () => {
    const { inicio, fin } = fechasParaTipo('quincenal');
    setFormProcesar({ fecha_inicio: inicio, fecha_fin: fin, tipo_periodo: 'quincenal', notas: '' });
    setPreview(null);
    setAjustes({});
    setModalProcesar(true);
  };

  const handleTipoPeriodoChange = (e) => {
    const tipo = e.target.value;
    const { inicio, fin } = fechasParaTipo(tipo);
    setFormProcesar(f => ({ ...f, tipo_periodo: tipo, fecha_inicio: inicio, fecha_fin: fin }));
    setPreview(null);
  };

  const calcularPreview = async () => {
    if (!formProcesar.fecha_inicio || !formProcesar.fecha_fin)
      return Swal.fire('Atención', 'Selecciona el rango de fechas', 'warning');
    setLoadPreview(true);
    try {
      const empleadosAjuste = Object.entries(ajustes).map(([id, data]) => ({
        empleado_id: parseInt(id), ...data,
      }));
      const { data } = await api.post('/nomina-universal/preview', {
        ...formProcesar, empleados: empleadosAjuste,
      });
      setPreview(data);
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || 'Error al calcular', 'error');
    } finally { setLoadPreview(false); }
  };

  const procesarNomina = async () => {
    const r = await Swal.fire({
      title: '¿Procesar nómina?',
      text: `Se generará la nómina para ${preview?.empleados || 0} empleados. ¿Confirmar?`,
      icon: 'question', showCancelButton: true,
      confirmButtonText: 'Sí, procesar', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0d6efd',
    });
    if (!r.isConfirmed) return;
    try {
      const empleadosAjuste = Object.entries(ajustes).map(([id, data]) => ({
        empleado_id: parseInt(id), ...data,
      }));
      await api.post('/nomina-universal', {
        ...formProcesar, empleados: empleadosAjuste,
      });
      Swal.fire({ icon: 'success', title: 'Nómina procesada', timer: 1800, showConfirmButton: false });
      setModalProcesar(false);
      cargarPeriodos();
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || 'Error al procesar', 'error');
    }
  };

  const marcarPagada = async (periodo) => {
    const r = await Swal.fire({
      title: `¿Marcar ${periodo.numero} como pagada?`,
      icon: 'question', showCancelButton: true,
      confirmButtonText: 'Sí, pagada', confirmButtonColor: '#28a745',
    });
    if (!r.isConfirmed) return;
    try {
      await api.put(`/nomina-universal/${periodo.id}/pagar`);
      Swal.fire({ icon: 'success', title: 'Marcada como pagada', timer: 1500, showConfirmButton: false });
      cargarPeriodos();
      if (detallePeriodo?.id === periodo.id) verDetalle(periodo.id);
    } catch (e) { Swal.fire('Error', e.response?.data?.error || 'Error', 'error'); }
  };

  const anularNomina = async (periodo) => {
    const r = await Swal.fire({
      title: `¿Anular ${periodo.numero}?`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Sí, anular', confirmButtonColor: '#dc3545',
    });
    if (!r.isConfirmed) return;
    try {
      await api.put(`/nomina-universal/${periodo.id}/anular`);
      Swal.fire({ icon: 'success', title: 'Anulada', timer: 1500, showConfirmButton: false });
      cargarPeriodos();
      if (detallePeriodo?.id === periodo.id) setTab('periodos');
    } catch (e) { Swal.fire('Error', e.response?.data?.error || 'Error', 'error'); }
  };

  const setAjuste = (emp_id, campo, valor) => {
    setAjustes(prev => ({
      ...prev,
      [emp_id]: { ...(prev[emp_id] || {}), empleado_id: emp_id, [campo]: parseFloat(valor) || 0 },
    }));
  };

  // ── Estilos ───────────────────────────────────────────────────────────────
  const st = {
    page:  { padding: '24px', fontFamily: 'sans-serif', maxWidth: 1200, margin: '0 auto' },
    card:  { background: '#fff', borderRadius: 12, boxShadow: '0 2px 10px #0001', padding: 24, marginBottom: 24 },
    btn:   { padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
    badge: (c) => ({ background: c, color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: 12 }),
    tbl:   { width: '100%', borderCollapse: 'collapse' },
    th:    { textAlign: 'left', padding: '10px 12px', background: '#f8f9fa', borderBottom: '2px solid #dee2e6', fontSize: 13 },
    td:    { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', fontSize: 14 },
    input: { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' },
    select:{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 },
    overlay:{ position: 'fixed', inset: 0, background: '#0007', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 40, overflowY: 'auto' },
    modal: { background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 900, margin: '0 auto 40px' },
    label: { display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600, color: '#444' },
    num:   { textAlign: 'right' },
    tabBtn:(active) => ({
      padding: '10px 20px', border: 'none', cursor: 'pointer', fontWeight: 600,
      borderBottom: active ? '3px solid #0d6efd' : '3px solid transparent',
      color: active ? '#0d6efd' : '#555', background: 'transparent',
    }),
  };

  const totalEmpleados = resumen?.totalEmpleados || 0;
  const ultimaNomina   = resumen?.ultima;

  return (
    <div style={st.page}>
      {/* Cabecera */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0 }}>💰 Nómina Universal</h2>
          <p style={{ margin: 0, color: '#888' }}>Procesamiento de nómina con TSS e ISR automáticos</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={{ ...st.btn, background: '#6f42c1', color: '#fff' }}
            onClick={() => navigate('/nomina/reporte-tss')}>
            🏥 Reporte TSS
          </button>
          <button style={{ ...st.btn, background: '#0d6efd', color: '#fff' }} onClick={abrirProcesar}>
            + Procesar Nómina
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Empleados Activos', value: totalEmpleados, color: '#0d6efd' },
          { label: 'Última Nómina', value: ultimaNomina?.numero || '—', color: '#6f42c1' },
          { label: 'Total Bruto (última)', value: ultimaNomina ? `RD$ ${fmt(ultimaNomina.total_bruto)}` : '—', color: '#fd7e14' },
          { label: 'Total Neto (última)',  value: ultimaNomina ? `RD$ ${fmt(ultimaNomina.total_neto)}`  : '—', color: '#28a745' },
        ].map(k => (
          <div key={k.label} style={{ ...st.card, marginBottom: 0, borderLeft: `4px solid ${k.color}`, padding: 16 }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #dee2e6', marginBottom: 24 }}>
        <button style={st.tabBtn(tab === 'periodos')} onClick={() => setTab('periodos')}>Períodos</button>
        {detallePeriodo && (
          <button style={st.tabBtn(tab === 'detalle')} onClick={() => setTab('detalle')}>
            Detalle: {detallePeriodo.numero}
          </button>
        )}
        <button style={st.tabBtn(tab === 'config')} onClick={() => setTab('config')}>⚙️ Configuración</button>
      </div>

      {/* Tab períodos */}
      {tab === 'periodos' && (
        <div style={st.card}>
          {loading ? <p style={{ textAlign: 'center', color: '#888' }}>Cargando...</p> : (
            <table style={st.tbl}>
              <thead>
                <tr>
                  {['Número','Período','Tipo','Empleados Aprox','Bruto','Desc.','Neto','Estado','Acciones'].map(h => (
                    <th key={h} style={st.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {periodos.length === 0 && (
                  <tr><td colSpan={9} style={{ ...st.td, textAlign: 'center', color: '#aaa' }}>
                    No hay nóminas procesadas aún
                  </td></tr>
                )}
                {periodos.map(p => (
                  <tr key={p.id}>
                    <td style={st.td}><code>{p.numero}</code></td>
                    <td style={st.td}>{p.fecha_inicio} → {p.fecha_fin}</td>
                    <td style={st.td}>{p.tipo_periodo}</td>
                    <td style={{ ...st.td, ...st.num }}>—</td>
                    <td style={{ ...st.td, ...st.num }}>RD$ {fmt(p.total_bruto)}</td>
                    <td style={{ ...st.td, ...st.num }}>RD$ {fmt(p.total_descuentos)}</td>
                    <td style={{ ...st.td, ...st.num, color: '#28a745', fontWeight: 700 }}>RD$ {fmt(p.total_neto)}</td>
                    <td style={st.td}>
                      <span style={st.badge(ESTADO_COLOR[p.estado] || '#888')}>{p.estado}</span>
                    </td>
                    <td style={st.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={{ ...st.btn, background: '#0d6efd', color: '#fff', padding: '5px 10px' }}
                          onClick={() => verDetalle(p.id)}>Ver</button>
                        {p.estado === 'procesada' && (
                          <button style={{ ...st.btn, background: '#28a745', color: '#fff', padding: '5px 10px' }}
                            onClick={() => marcarPagada(p)}>Pagar</button>
                        )}
                        {(p.estado === 'borrador' || p.estado === 'procesada') && (
                          <button style={{ ...st.btn, background: '#dc3545', color: '#fff', padding: '5px 10px' }}
                            onClick={() => anularNomina(p)}>Anular</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Tab detalle */}
      {tab === 'detalle' && detallePeriodo && (
        <div>
          {/* Resumen período */}
          <div style={{ ...st.card, borderLeft: `4px solid ${ESTADO_COLOR[detallePeriodo.estado]}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h3 style={{ margin: 0 }}>{detallePeriodo.numero}</h3>
                <p style={{ margin: 0, color: '#888' }}>{detallePeriodo.fecha_inicio} → {detallePeriodo.fecha_fin} | {detallePeriodo.tipo_periodo}</p>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ textAlign: 'center', padding: '8px 16px', background: '#f8f9fa', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: '#888' }}>Bruto Total</div>
                  <div style={{ fontWeight: 700 }}>RD$ {fmt(detallePeriodo.total_bruto)}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '8px 16px', background: '#fff3cd', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: '#888' }}>TSS Patronal</div>
                  <div style={{ fontWeight: 700 }}>RD$ {fmt(detallePeriodo.total_tss_patronal)}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '8px 16px', background: '#d4edda', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: '#888' }}>Neto a Pagar</div>
                  <div style={{ fontWeight: 700, color: '#28a745' }}>RD$ {fmt(detallePeriodo.total_neto)}</div>
                </div>
                <span style={st.badge(ESTADO_COLOR[detallePeriodo.estado] || '#888')}>
                  {detallePeriodo.estado}
                </span>
              </div>
            </div>
          </div>

          {/* Tabla detalles */}
          <div style={st.card}>
            <h4 style={{ marginTop: 0 }}>Desglose por Empleado</h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={st.tbl}>
                <thead>
                  <tr>
                    {['Empleado','Cargo','Días','Bruto','TSS Emp (5.91%)','ISR','Otros Desc.','Total Desc.','TSS Pat (15.29%)','Neto','Estado Pago'].map(h => (
                      <th key={h} style={{ ...st.th, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(detallePeriodo.detalles || []).map(d => (
                    <tr key={d.id} style={{ background: d.pagado ? '#f0fff4' : '#fff' }}>
                      <td style={st.td}><strong>{d.empleado?.nombre || '—'}</strong></td>
                      <td style={st.td}>{d.empleado?.cargo?.nombre || '—'}</td>
                      <td style={{ ...st.td, ...st.num }}>{d.dias_trabajados}</td>
                      <td style={{ ...st.td, ...st.num }}>RD$ {fmt(d.total_ingresos)}</td>
                      <td style={{ ...st.td, ...st.num, color: '#fd7e14' }}>RD$ {fmt(d.tss_empleado)}</td>
                      <td style={{ ...st.td, ...st.num, color: '#dc3545' }}>RD$ {fmt(d.isr_retenido)}</td>
                      <td style={{ ...st.td, ...st.num }}>RD$ {fmt(d.otros_descuentos)}</td>
                      <td style={{ ...st.td, ...st.num, color: '#dc3545' }}>RD$ {fmt(d.total_descuentos)}</td>
                      <td style={{ ...st.td, ...st.num, color: '#6f42c1' }}>RD$ {fmt(d.tss_patronal)}</td>
                      <td style={{ ...st.td, ...st.num, fontWeight: 700, color: '#28a745' }}>RD$ {fmt(d.salario_neto)}</td>
                      <td style={st.td}>
                        <span style={st.badge(d.pagado ? '#28a745' : '#6c757d')}>
                          {d.pagado ? `Pagado ${d.fecha_pago || ''}` : 'Pendiente'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f8f9fa', fontWeight: 700 }}>
                    <td colSpan={3} style={st.td}>TOTALES</td>
                    <td style={{ ...st.td, ...st.num }}>RD$ {fmt(detallePeriodo.total_bruto)}</td>
                    <td style={{ ...st.td, ...st.num }}>
                      RD$ {fmt((detallePeriodo.detalles || []).reduce((s, d) => s + parseFloat(d.tss_empleado || 0), 0))}
                    </td>
                    <td style={{ ...st.td, ...st.num }}>
                      RD$ {fmt((detallePeriodo.detalles || []).reduce((s, d) => s + parseFloat(d.isr_retenido || 0), 0))}
                    </td>
                    <td style={{ ...st.td, ...st.num }}>—</td>
                    <td style={{ ...st.td, ...st.num }}>RD$ {fmt(detallePeriodo.total_descuentos)}</td>
                    <td style={{ ...st.td, ...st.num }}>RD$ {fmt(detallePeriodo.total_tss_patronal)}</td>
                    <td style={{ ...st.td, ...st.num, color: '#28a745' }}>RD$ {fmt(detallePeriodo.total_neto)}</td>
                    <td style={st.td}></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
              {detallePeriodo.estado === 'procesada' && (
                <button style={{ ...st.btn, background: '#28a745', color: '#fff' }}
                  onClick={() => marcarPagada(detallePeriodo)}>Marcar como Pagada</button>
              )}
              {(detallePeriodo.estado === 'borrador' || detallePeriodo.estado === 'procesada') && (
                <button style={{ ...st.btn, background: '#dc3545', color: '#fff' }}
                  onClick={() => anularNomina(detallePeriodo)}>Anular Nómina</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab configuración */}
      {tab === 'config' && (
        <div style={st.card}>
          <h3 style={{ marginTop: 0 }}>⚙️ Configuración de Nómina</h3>
          <div style={{ maxWidth: 420 }}>
            <label style={st.label}>Quincena de descuento TSS e ISR</label>
            <select
              value={quincenaDescuento}
              onChange={(e) => setQuincenaDescuento(e.target.value)}
              style={st.select}
            >
              <option value="primera">Primera quincena (1-15)</option>
              <option value="segunda">Segunda quincena (16-fin de mes)</option>
            </select>
            <p style={{ fontSize: 12, color: '#888', margin: '8px 0 20px' }}>
              Define en qué quincena se aplican SFS, AFP e ISR al procesar nómina quincenal.
              La quincena opuesta se procesa sin descuentos.
            </p>
            <button
              style={{ ...st.btn, background: '#0d6efd', color: '#fff' }}
              onClick={guardarConfigNomina}
              disabled={guardandoConfig}
            >
              {guardandoConfig ? 'Guardando...' : '💾 Guardar'}
            </button>
          </div>
        </div>
      )}

      {/* Modal procesar nómina */}
      {modalProcesar && (
        <div style={st.overlay} onClick={e => e.target === e.currentTarget && setModalProcesar(false)}>
          <div style={st.modal}>
            <h3 style={{ marginTop: 0 }}>Procesar Nueva Nómina</h3>

            {/* Parámetros */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div>
                <label style={st.label}>Fecha Inicio</label>
                <input type="date" style={st.input} value={formProcesar.fecha_inicio}
                  onChange={e => setFormProcesar(f => ({ ...f, fecha_inicio: e.target.value }))} />
              </div>
              <div>
                <label style={st.label}>Fecha Fin</label>
                <input type="date" style={st.input} value={formProcesar.fecha_fin}
                  onChange={e => setFormProcesar(f => ({ ...f, fecha_fin: e.target.value }))} />
              </div>
              <div>
                <label style={st.label}>Tipo de Período</label>
                <select style={st.select} value={formProcesar.tipo_periodo}
                  onChange={handleTipoPeriodoChange}>
                  <option value="quincenal">Quincenal</option>
                  <option value="mensual">Mensual</option>
                  <option value="semanal">Semanal</option>
                </select>
              </div>
            </div>

            {/* Info quincena */}
            {formProcesar.tipo_periodo === 'quincenal' && formProcesar.fecha_inicio && (() => {
              const dia = new Date(formProcesar.fecha_inicio + 'T00:00:00').getDate();
              const esSegunda = dia >= 16;
              return (
                <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8,
                  background: esSegunda ? '#fff3cd' : '#d1ecf1',
                  border: `1px solid ${esSegunda ? '#ffc107' : '#bee5eb'}`,
                  fontSize: 13, color: '#333' }}>
                  {esSegunda
                    ? '⚡ 2ª quincena — Sin descuentos de TSS ni ISR (ya aplicados en la 1ª quincena)'
                    : '📋 1ª quincena — Se descuentan SFS + AFP + ISR sobre el salario mensual completo'}
                </div>
              );
            })()}
            <div style={{ marginBottom: 20 }}>
              <label style={st.label}>Notas</label>
              <input style={st.input} value={formProcesar.notas}
                onChange={e => setFormProcesar(f => ({ ...f, notas: e.target.value }))} />
            </div>

            <button style={{ ...st.btn, background: '#6f42c1', color: '#fff', marginBottom: 20 }}
              onClick={calcularPreview} disabled={loadPreview}>
              {loadPreview ? 'Calculando...' : '🔢 Calcular Preview'}
            </button>

            {/* Tabla preview */}
            {preview && (
              <div>
                {/* Totales preview */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
                  {[
                    { label: 'Empleados', value: preview.empleados, color: '#0d6efd' },
                    { label: 'Bruto Total', value: `RD$ ${fmt(preview.total_bruto)}`, color: '#fd7e14' },
                    { label: 'TSS Patronal', value: `RD$ ${fmt(preview.total_tss_patronal)}`, color: '#6f42c1' },
                    { label: 'Neto a Pagar', value: `RD$ ${fmt(preview.total_neto)}`, color: '#28a745' },
                  ].map(k => (
                    <div key={k.label} style={{ background: '#f8f9fa', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#888' }}>{k.label}</div>
                      <div style={{ fontWeight: 700, color: k.color, fontSize: 15 }}>{k.value}</div>
                    </div>
                  ))}
                </div>

                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                  <table style={st.tbl}>
                    <thead>
                      <tr>
                        {['Empleado','Salario Base','Días','Bruto','TSS Emp','ISR','Neto','Ajustes'].map(h => (
                          <th key={h} style={{ ...st.th, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.detalles.map(d => (
                        <tr key={d.empleado_id}>
                          <td style={st.td}>{d.empleado?.nombre || `Emp #${d.empleado_id}`}</td>
                          <td style={{ ...st.td, ...st.num }}>RD$ {fmt(d.empleado?.salario_base)}</td>
                          <td style={{ ...st.td, ...st.num }}>
                            <input type="number" min={0} max={31} step={0.5}
                              defaultValue={d.dias_trabajados}
                              style={{ ...st.input, width: 60, padding: '4px 6px' }}
                              onChange={e => setAjuste(d.empleado_id, 'dias_trabajados', e.target.value)} />
                          </td>
                          <td style={{ ...st.td, ...st.num }}>RD$ {fmt(d.salario_bruto)}</td>
                          <td style={{ ...st.td, ...st.num, color: '#fd7e14' }}>RD$ {fmt(d.tss_empleado)}</td>
                          <td style={{ ...st.td, ...st.num, color: '#dc3545' }}>RD$ {fmt(d.isr_retenido)}</td>
                          <td style={{ ...st.td, ...st.num, fontWeight: 700, color: '#28a745' }}>RD$ {fmt(d.salario_neto)}</td>
                          <td style={st.td}>
                            <input type="number" min={0} step={0.01} placeholder="Bonus"
                              style={{ ...st.input, width: 80, padding: '4px 6px' }}
                              onChange={e => setAjuste(d.empleado_id, 'bonificaciones', e.target.value)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                  * TSS Empleado = 5.91% (SFS 3.04% + AFP 2.87%) | TSS Patronal = 15.29% (SFS 7.09% + AFP 7.10% + SRL 1.10%)
                  <br/>* ISR calculado según escala progresiva DGII 2026 (Norma 01-2025)
                  <br/>* Modifica días trabajados o bonificaciones y vuelve a calcular para actualizar
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button style={{ ...st.btn, background: '#6c757d', color: '#fff' }}
                onClick={() => setModalProcesar(false)}>Cancelar</button>
              {preview && (
                <button style={{ ...st.btn, background: '#0d6efd', color: '#fff' }}
                  onClick={procesarNomina}>
                  ✅ Procesar Nómina ({preview.empleados} empleados)
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
